import { useCallback, useMemo, memo } from "react";
import { Fade, Stack } from "@mui/material";
import { ProductFilter } from "../../filters/ProductFilter/ProductFilter";
import { PRODUCT_LIST_UPLOAD_PROPS } from "../../filters/ProductFilter/constants";
import { ProductFilterValue } from "../../filters/ProductFilter/types";
import { FaiFilterSection } from "../../filters/UserFilters/FaiFilter/FaiFilterSection";
import { EXPORT_INCLUSION_TYPES } from "../types";

const MemoizedFaiSelectedProducts = memo(
  ({
    selectedProductIds,
    onProductsSelect,
    faiSelectedIds,
    onFaiSelect,
    showFaiRefinement,
  }: {
    selectedProductIds: number[];
    onProductsSelect: (value: ProductFilterValue) => void;
    faiSelectedIds: string[];
    onFaiSelect: (departmentIds: string[], roleIds: string[]) => void;
    showFaiRefinement: boolean;
  }) => (
    <Fade in={true} timeout={300}>
      <Stack sx={{ mx: 4 }} spacing={2}>
        <ProductFilter
          selectedProductIds={selectedProductIds}
          onProductsSelect={onProductsSelect}
          uploadProps={PRODUCT_LIST_UPLOAD_PROPS}
          autoCompleteProps={{ disablePortal: false }}
        />
        {showFaiRefinement && (
          <FaiFilterSection
            id="fai-export-department-role-input"
            selectedIds={faiSelectedIds}
            onFaiSelect={onFaiSelect}
          />
        )}
      </Stack>
    </Fade>
  )
);

MemoizedFaiSelectedProducts.displayName = "MemoizedFaiSelectedProducts";

export const useFaiSelectedRenderer = (
  inclusion: EXPORT_INCLUSION_TYPES,
  selectedProductIds: number[],
  onProductsSelect: (ids: number[]) => void,
  faiDepartmentIds: string[],
  faiRoleIds: string[],
  onFaiSelect: (departmentIds: string[], roleIds: string[]) => void,
  // Only the Universe payload carries the department/role refinement.
  showFaiRefinement: boolean
) => {
  const handleProductsSelect = useCallback(
    (value: ProductFilterValue) => onProductsSelect(value.ids),
    [onProductsSelect]
  );

  const faiSelectedIds = useMemo(
    () => [...faiDepartmentIds, ...faiRoleIds],
    [faiDepartmentIds, faiRoleIds]
  );

  return useCallback(
    (optionValue: string) => {
      if (
        optionValue === EXPORT_INCLUSION_TYPES.SELECTED &&
        inclusion === EXPORT_INCLUSION_TYPES.SELECTED
      ) {
        return (
          <MemoizedFaiSelectedProducts
            selectedProductIds={selectedProductIds}
            onProductsSelect={handleProductsSelect}
            faiSelectedIds={faiSelectedIds}
            onFaiSelect={onFaiSelect}
            showFaiRefinement={showFaiRefinement}
          />
        );
      }
      return null;
    },
    [
      inclusion,
      selectedProductIds,
      handleProductsSelect,
      faiSelectedIds,
      onFaiSelect,
      showFaiRefinement,
    ]
  );
};
